/** FuryBot — Match Engine Flow (Help me pick a firm) */
import axios from "axios";
import { BOT_API } from "../constants";
import { track } from "../utils/analytics";
import { matchFirms, createFirmCard, getWhyBullets } from "../utils/dataService";

export function createMatchFlowHandlers(ctx) {
  const { firmsRef, promosRef, addBot, setMessages, setFirms, ensureFirms, matchPrefs, setMatchPrefs, setMatchStep } = ctx;

  const startMatchFlow = () => {
    track("flow_started", { flow: "match" });
    setMatchStep("budget");
    ensureFirms();
    addBot("Let's find your ideal firm. First — what's your budget for an evaluation?", [
      { label: "Under $100", action: "match_budget", data: "under100" },
      { label: "$100-$200", action: "match_budget", data: "100-200" },
      { label: "$200-$400", action: "match_budget", data: "200-400" },
      { label: "Don't care", action: "match_budget", data: "any" },
    ]);
  };

  const handleMatchStep = (action, value) => {
    const step = action.replace("match_", "");
    const newPrefs = { ...matchPrefs, [step]: value };
    setMatchPrefs(newPrefs);

    if (step === "budget") {
      setMatchStep("style");
      addBot("How do you trade?", [
        { label: "Scalping", action: "match_style", data: "scalping" },
        { label: "Day trading", action: "match_style", data: "day" },
        { label: "Swing trading", action: "match_style", data: "swing" },
        { label: "Mix of styles", action: "match_style", data: "mix" },
      ]);
    } else if (step === "style") {
      setMatchStep("experience");
      addBot("What's your prop firm experience?", [
        { label: "First time", action: "match_experience", data: "first" },
        { label: "1-2 evals before", action: "match_experience", data: "some" },
        { label: "Experienced", action: "match_experience", data: "experienced" },
      ]);
    } else if (step === "experience") {
      setMatchStep("priority");
      addBot("What matters most to you?", [
        { label: "Cheapest eval", action: "match_priority", data: "cheapest" },
        { label: "Forgiving drawdown", action: "match_priority", data: "drawdown" },
        { label: "Best profit split", action: "match_priority", data: "split" },
        { label: "Fast payouts", action: "match_priority", data: "payout" },
      ]);
    } else if (step === "priority") {
      if (firmsRef.current.length === 0) {
        addBot("Finding your best matches...", []);
        axios.get(`${BOT_API}/firms`).then(r => {
          firmsRef.current = r.data || [];
          setFirms(r.data || []);
          showMatchResults(newPrefs);
        }).catch(() => showMatchResults(newPrefs));
        return;
      }
      showMatchResults(newPrefs);
    }
  };

  const showMatchResults = (prefs) => {
    const f = firmsRef.current;
    const p = promosRef.current;
    if (f.length === 0) {
      addBot("I'm pulling the latest firm data right now. You can wait a second, or jump straight to our comparison and deals pages below.", [
        { label: "Compare prop firms", action: "link", data: "/futures-prop-firm-comparison" },
        { label: "View live deals", action: "link", data: "/futures-prop-firm-discounts" },
        { label: "Try again", action: "retry_match", data: JSON.stringify(prefs) },
      ]);
      axios.get(`${BOT_API}/firms`).then(r => { firmsRef.current = r.data || []; setFirms(r.data || []); }).catch(() => {});
      setMatchStep(null);
      return;
    }
    const results = matchFirms(f, prefs, p);
    const cards = results.map(r => createFirmCard(r.firm, r.reasons.join(" · "), p));
    track("firm_shown", { firms: results.map(r => r.firm.name) });
    const bullets = getWhyBullets(prefs);
    const whyText = bullets.length > 0 ? `These firms match your trading style because they offer:\n${bullets.map(b => `• ${b}`).join("\n")}` : "Here are your top matches based on your answers:";
    setMessages(m => [...m, { role: "bot", text: whyText, cards, buttons: [
      { label: "Compare these", action: "flow_compare" },
      { label: "Show live deals", action: "flow_deals" },
      { label: "Explain drawdown rules", action: "flow_rules" },
      { label: "Start over", action: "restart_match" },
    ], ts: Date.now() }]);
    setMatchStep(null);
  };

  return { startMatchFlow, handleMatchStep, showMatchResults };
}
